/**
 * User Management Table
 *
 * Admin panel component for managing users:
 * - List all users with role, status, MFA status
 * - Create new users with force MFA option
 * - Update user details (name, role, force MFA)
 * - Delete users
 * - Force MFA enrollment
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import {
  UserPlus,
  Edit2,
  Trash2,
  Shield,
  CheckCircle,
  XCircle,
  AlertTriangle,
} from 'lucide-react';

// =============================================================================
// Types
// =============================================================================

interface User {
  user_id: string;
  email: string;
  name: string;
  role: 'super_admin' | 'admin' | 'user';
  is_active: boolean;
  is_verified: boolean;
  mfa_enabled: boolean;
  mfa_required: boolean;
  created_at: string;
  last_login_at: string | null;
}

// =============================================================================
// Component
// =============================================================================

export default function UserManagementTable() {
  const { getAuthToken } = useAuth();
  const API_BASE = import.meta.env.VITE_API_URL || 'https://claude-essay-agent-production.up.railway.app';

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create user modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createEmail, setCreateEmail] = useState('');
  const [createName, setCreateName] = useState('');
  const [createRole, setCreateRole] = useState<'user' | 'admin'>('user');
  const [createForceMFA, setCreateForceMFA] = useState(false);
  const [creating, setCreating] = useState(false);

  // Edit user modal
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [editName, setEditName] = useState('');
  const [editRole, setEditRole] = useState<'user' | 'admin'>('user');
  const [editForceMFA, setEditForceMFA] = useState(false);
  const [editing, setEditing] = useState(false);

  // ---------------------------------------------------------------------------
  // Load Users
  // ---------------------------------------------------------------------------

  useEffect(() => {
    loadUsers();
  }, []);

  async function loadUsers() {
    try {
      setLoading(true);
      setError(null);

      const token = await getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_BASE}/api/v1/admin/users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to load users');
      }

      const data = await response.json();
      setUsers(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Create User
  // ---------------------------------------------------------------------------

  async function handleCreateUser(e: React.FormEvent) {
    e.preventDefault();

    try {
      setCreating(true);
      setError(null);

      const token = await getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_BASE}/api/v1/admin/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: createEmail,
          name: createName,
          role: createRole,
          force_mfa: createForceMFA,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to create user');
      }

      // Success - reload users
      await loadUsers();
      setShowCreateModal(false);
      setCreateEmail('');
      setCreateName('');
      setCreateRole('user');
      setCreateForceMFA(false);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Edit User
  // ---------------------------------------------------------------------------

  function openEditModal(user: User) {
    setEditingUser(user);
    setEditName(user.name);
    setEditRole(user.role as 'user' | 'admin');
    setEditForceMFA(user.mfa_required);
  }

  async function handleEditUser(e: React.FormEvent) {
    e.preventDefault();

    if (!editingUser) return;

    try {
      setEditing(true);
      setError(null);

      const token = await getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_BASE}/api/v1/admin/users/${editingUser.user_id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: editName,
          role: editRole,
          force_mfa: editForceMFA,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to update user');
      }

      // Success - reload users
      await loadUsers();
      setEditingUser(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setEditing(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Delete User
  // ---------------------------------------------------------------------------

  async function handleDeleteUser(userId: string, userEmail: string) {
    if (!confirm(`Delete user ${userEmail}? This action cannot be undone.`)) {
      return;
    }

    try {
      setError(null);

      const token = await getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_BASE}/api/v1/admin/users/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to delete user');
      }

      // Success - reload users
      await loadUsers();
    } catch (err: any) {
      setError(err.message);
    }
  }

  // ---------------------------------------------------------------------------
  // Force MFA
  // ---------------------------------------------------------------------------

  async function handleForceMFA(userId: string, userEmail: string) {
    if (!confirm(`Force MFA enrollment for ${userEmail}?`)) {
      return;
    }

    try {
      setError(null);

      const token = await getAuthToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_BASE}/api/v1/admin/users/${userId}/force-mfa`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to force MFA');
      }

      // Success - reload users
      await loadUsers();
    } catch (err: any) {
      setError(err.message);
    }
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-dark">User Management</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center gap-2"
        >
          <UserPlus size={20} />
          Create User
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                MFA
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user) => (
              <tr key={user.user_id}>
                <td className="px-6 py-4">
                  <div>
                    <div className="font-medium text-dark">{user.name}</div>
                    <div className="text-sm text-gray-500">{user.email}</div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 text-xs font-medium rounded ${
                    user.role === 'super_admin' ? 'bg-purple-100 text-purple-700' :
                    user.role === 'admin' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {user.role}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    {user.is_active ? (
                      <CheckCircle size={16} className="text-green-500" />
                    ) : (
                      <XCircle size={16} className="text-red-500" />
                    )}
                    <span className="text-sm">
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    {user.mfa_enabled ? (
                      <Shield size={16} className="text-green-500" />
                    ) : user.mfa_required ? (
                      <AlertTriangle size={16} className="text-orange-500" />
                    ) : (
                      <Shield size={16} className="text-gray-300" />
                    )}
                    <span className="text-sm">
                      {user.mfa_enabled ? 'Enrolled' : user.mfa_required ? 'Required' : 'Optional'}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 text-right space-x-2">
                  <button
                    onClick={() => openEditModal(user)}
                    className="text-primary hover:text-cyan"
                  >
                    <Edit2 size={18} />
                  </button>
                  {!user.mfa_enabled && (
                    <button
                      onClick={() => handleForceMFA(user.user_id, user.email)}
                      className="text-orange-500 hover:text-orange-600"
                      title="Force MFA enrollment"
                    >
                      <Shield size={18} />
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteUser(user.user_id, user.email)}
                    className="text-red-500 hover:text-red-600"
                  >
                    <Trash2 size={18} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg p-6 max-w-md w-full"
          >
            <h3 className="text-xl font-bold mb-4">Create New User</h3>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={createEmail}
                  onChange={(e) => setCreateEmail(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <select
                  value={createRole}
                  onChange={(e) => setCreateRole(e.target.value as 'user' | 'admin')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="forceMFA"
                  checked={createForceMFA}
                  onChange={(e) => setCreateForceMFA(e.target.checked)}
                  className="mr-2"
                />
                <label htmlFor="forceMFA" className="text-sm text-gray-700">
                  Require MFA enrollment before dashboard access
                </label>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn-secondary"
                  disabled={creating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={creating}
                >
                  {creating ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg p-6 max-w-md w-full"
          >
            <h3 className="text-xl font-bold mb-4">Edit User</h3>
            <form onSubmit={handleEditUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={editingUser.email}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <select
                  value={editRole}
                  onChange={(e) => setEditRole(e.target.value as 'user' | 'admin')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="editForceMFA"
                  checked={editForceMFA}
                  onChange={(e) => setEditForceMFA(e.target.checked)}
                  className="mr-2"
                />
                <label htmlFor="editForceMFA" className="text-sm text-gray-700">
                  Require MFA enrollment
                </label>
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setEditingUser(null)}
                  className="btn-secondary"
                  disabled={editing}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={editing}
                >
                  {editing ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
}
